import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import { Anchor, Badge, Button, Card, Collapse, Group, Stack, Table, Text } from "@mantine/core"
import { modals } from "@mantine/modals"
import { IconMicrophone, IconTrash } from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import { useEffect, useRef } from "react"
import { describeEnrollmentSource } from "../../../lib/speaker-enrollment-source"
import { LineListSkeleton } from "../../PageSkeletons"
import type { IViewProps } from "./interfaces"

/**
 * Dumb view for a single "known voice" in the tenant voiceprint library. Renders
 * the identity header (name / title / email) with a "recognised in N meetings"
 * badge — the visible voiceprint moat — and, when expanded, the voiceprint sample
 * history (source, meeting, label, enrolled-at) with per-sample revoke plus a
 * delete-identity action. Props in, events out: the smart wrapper owns the sample
 * query and the mutations.
 */
export function KnownVoiceCardView({
  name,
  title,
  email,
  recognizedMeetingCount,
  expanded,
  onToggle,
  samples,
  samplesLoading,
  samplesError,
  onRevoke,
  onDelete,
  deleting = false,
  highlighted = false,
}: IViewProps): React.JSX.Element {
  const { i18n } = useLingui()
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!highlighted) return
    cardRef.current?.scrollIntoView({ block: "center", behavior: "smooth" })
  }, [highlighted])

  const confirmRevoke = (sampleId: string): void => {
    modals.openConfirmModal({
      title: i18n._(msg`Revoke this voice sample?`),
      children: (
        <Text size="sm">
          <Trans>The sample is deleted permanently. Other samples for this voice stay.</Trans>
        </Text>
      ),
      labels: { confirm: i18n._(msg`Revoke sample`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: () => onRevoke(sampleId),
    })
  }

  const confirmDelete = (): void => {
    modals.openConfirmModal({
      title: i18n._(msg`Delete ${name}?`),
      children: (
        <Text size="sm">
          <Trans>This removes the identity and every voice sample. It cannot be undone.</Trans>
        </Text>
      ),
      labels: { confirm: i18n._(msg`Delete voice`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: onDelete,
    })
  }

  return (
    <Card withBorder padding="sm" ref={cardRef} data-highlighted={highlighted ? "true" : undefined}>
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Group gap="sm" wrap="wrap" style={{ minWidth: 0 }}>
          <Text fw={600}>{name}</Text>
          {title && (
            <Text c="dimmed" size="sm">
              {title}
            </Text>
          )}
          {email && (
            <Text c="dimmed" size="xs" ff="monospace">
              {email}
            </Text>
          )}
          <Badge
            variant="light"
            color={recognizedMeetingCount > 0 ? "brand" : "gray"}
            leftSection={<IconMicrophone size={11} />}
          >
            {recognizedMeetingCount > 0 ? (
              <Plural value={recognizedMeetingCount} one="Recognised in # meeting" other="Recognised in # meetings" />
            ) : (
              <Trans>Not yet recognised</Trans>
            )}
          </Badge>
        </Group>
        <Button variant="subtle" size="xs" onClick={onToggle} style={{ flexShrink: 0 }}>
          {expanded ? <Trans>Hide samples</Trans> : <Trans>Show samples</Trans>}
        </Button>
      </Group>

      <Collapse in={expanded}>
        <Stack gap="xs" mt="md">
          {samplesLoading && <LineListSkeleton rows={3} />}
          {!samplesLoading && samplesError && (
            <Text c="danger" size="sm">
              <Trans>Couldn't load voice samples: {samplesError}</Trans>
            </Text>
          )}
          {!samplesLoading && !samplesError && samples.length === 0 && (
            <Text c="dimmed" size="sm">
              <Trans>No voiceprint samples yet — this speaker has been named but never voice-enrolled.</Trans>
            </Text>
          )}
          {!samplesLoading && samples.length > 0 && (
            <Table highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    <Trans>Source</Trans>
                  </Table.Th>
                  <Table.Th>
                    <Trans>Meeting</Trans>
                  </Table.Th>
                  <Table.Th>
                    <Trans>Label</Trans>
                  </Table.Th>
                  <Table.Th>
                    <Trans>Enrolled at</Trans>
                  </Table.Th>
                  <Table.Th style={{ width: 60 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {samples.map((sample) => {
                  const src = describeEnrollmentSource(sample.confirmed_by, i18n)
                  return (
                    <Table.Tr key={sample.id}>
                      <Table.Td>
                        <Badge color={src.color} variant="light" title={src.tooltip} tt="none">
                          {src.label}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {sample.source_meeting_id ? (
                          <Anchor
                            size="sm"
                            renderRoot={(props) => (
                              <Link to="/meetings/$id" params={{ id: sample.source_meeting_id ?? "" }} {...props} />
                            )}
                          >
                            {sample.meeting_subject ?? <Trans>Open meeting</Trans>}
                          </Anchor>
                        ) : (
                          <Text size="sm" c="dimmed">
                            {sample.meeting_subject ?? "—"}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" ff="monospace">
                          {sample.diarization_label ?? "—"}
                        </Text>
                        {sample.display_name &&
                          sample.display_name !== name &&
                          !sample.display_name.startsWith("encgc") && (
                            <Text size="xs" c="dimmed">
                              <Trans>(displayed as {sample.display_name})</Trans>
                            </Text>
                          )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">
                          {(() => {
                            const raw = sample.created_at.includes("T")
                              ? sample.created_at
                              : sample.created_at.replace(" ", "T")
                            const day = raw.slice(0, 10)
                            const time = raw.slice(11, 16)
                            return time ? `${day} ${time}` : day
                          })()}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color="danger"
                          aria-label={i18n._(msg`Revoke sample`)}
                          onClick={() => confirmRevoke(sample.id)}
                        >
                          <Trans>Revoke</Trans>
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          )}

          <Group justify="flex-end" mt="xs">
            <Button
              variant="subtle"
              color="danger"
              size="compact-xs"
              leftSection={<IconTrash size={12} />}
              loading={deleting}
              onClick={confirmDelete}
            >
              <Trans>Delete voice</Trans>
            </Button>
          </Group>
        </Stack>
      </Collapse>
    </Card>
  )
}
